import { t } from '@app/i18n';
import { Link } from 'react-router-dom';
import { InfoTip, LoadingButton } from '@zarya/ui';
import { errorView } from '@app/errors';
import { hourSeconds } from '@app/format';
import { causaOf, causaVocab, decisionKind, decisionMethod, type Decision } from '@app/main/Decisions';
import {
  askTrail,
  conversationSeconds,
  pdpRowText,
  refusalPhrase,
  refusalTone,
  toolCallLines,
  trailNotice,
  type AgentAskResult,
  type AskTrailRow,
} from './ChatModel';
import {
  conversationRange,
  decisionsLink,
  DECISIONS_PATH,
  type ConversationWindow,
} from './ChatEntryModel';
import { useConversationDecisions, type ConversationDecisions } from './conversationDecisions';
import type { HopStatus } from '@app/main/Conversar';
import s from './Chat.module.css';

/** Marca e prefixo acessível de cada situação — as mesmas de `HopTrail`. */
/**
 * Estado do salto → marca e texto para leitor de tela.
 *
 * O `spoken` sai do catálogo: as chaves (`ok`/`negado`/`falhou`) são o código que
 * o backend anota no salto e não se traduzem; o que se lê em voz alta, sim. Os
 * símbolos (✓ ⊘ ✕) não são texto de idioma e ficam.
 */
const MARK: Record<Exclude<HopStatus, null>, { symbol: string; className: string; spokenKey: string }> = {
  ok: { symbol: '✓', className: s.markOk, spokenKey: 'ok' },
  negado: { symbol: '⊘', className: s.markDenied, spokenKey: 'denied' },
  falhou: { symbol: '✕', className: s.markFailed, spokenKey: 'failed' },
};

/**
 * Tom do chip por situação — vai em todo passo (`data-status` + classe); o CSS
 * só pinta o chip inteiro no passo «Decisão», os outros pintam a marca.
 */
const STEP_TONE: Record<Exclude<HopStatus, null>, string> = {
  ok: s.stepOk,
  negado: s.stepDenied,
  falhou: s.stepFailed,
};

/**
 * A trilha compacta sob cada resposta (§4): Pessoa → Agente → «escolheu:
 * cobranca · faturas.consultar» → Decisão, em uma linha, com a marca de cada
 * passo. As recusas do contrato («pediu fora do contrato: …») vêm abaixo — não
 * são passos: são o que **não** aconteceu, e a fronteira que a demo existe para
 * mostrar.
 */
export interface AskTrailProps {
  result: AgentAskResult;
  /**
   * The conversation (aim-6ph.9): the agent asked, the person and the window of the question. With it, the trail lists
   * the PDP decisions of the conversation, and the link «Ver as decisões desta conversa» shows. Absent when the screen
   * does not know the window (a message without its times), and then the two do not show.
   */
  conversation?: ConversationWindow;
}

export function AskTrail({ result, conversation }: AskTrailProps) {
  const pdp = useConversationDecisions(conversation ?? null);
  const rows = askTrail(result, pdp.items);
  const refusals = result.refusals ?? [];
  const notice = trailNotice(result);
  const toolCalls = notice === 'ag_ui' ? toolCallLines(result) : [];

  return (
    <div className={s.trail}>
      <ol className={s.trailRow} aria-label={t('chat.trail.label')}>
        {rows.map((row, i) => (
          <li key={row.id} className={s.trailStep}>
            {i > 0 ? (
              <span className={s.trailArrow} aria-hidden="true">
                →
              </span>
            ) : null}
            <TrailStep row={row} />
          </li>
        ))}
      </ol>
      {refusals.length > 0 ? (
        <ul className={s.refusals} aria-label={t('chat.trail.refusalsLabel')}>
          {refusals.map((refusal, i) => {
            // `denied_stop` é o agente se comportando: ✓, não ⊘.
            const positive = refusalTone(refusal) === 'positive';
            return (
              <li
                key={`${refusal.agent}-${refusal.tool}-${i}`}
                className={s.refusal}
                data-tone={positive ? 'positive' : 'denied'}
              >
                <span className={`${s.mark} ${positive ? s.markOk : s.markDenied}`} aria-hidden="true">
                  {positive ? '✓' : '⊘'}
                </span>
                <span>{refusalPhrase(refusal)}</span>
                <code className={s.refusalReason}>{refusal.reason}</code>
              </li>
            );
          })}
        </ul>
      ) : null}
      {toolCalls.length > 0 ? (
        <ul className={s.toolCalls} aria-label={t('chat.trail.agUiTools')} data-testid="chat-tool-calls">
          {toolCalls.map((call, i) => (
            <li key={`${call.name}-${i}`} className={s.toolCall} data-received={call.received ? 'true' : 'false'}>
              <span className={s.stepLabel}>{t('chat.trail.toolCall')}</span>
              <code className={s.mono}>{call.name}</code>
              <span>
                {' · '}
                {t(call.received ? 'chat.trail.toolResult.received' : 'chat.trail.toolResult.missing')}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {notice ? (
        <p className={s.trailNotice} role="note" data-trail-notice={notice}>
          <span>{t(notice === 'none' ? 'chat.trail.notReported' : 'chat.trail.agUiTools')}</span>
        </p>
      ) : null}
      {conversation ? <PdpDecisions conversation={conversation} pdp={pdp} /> : null}
      {/*
        «Tempo da conversa» in the place of «ms · id» (aim-6ph.9): the id was the one of the call to the executor, and it
        read as the id of a decision. The link shows in every answer with a window, and not only when the agent does not
        report its choices.
      */}
      <p className={s.conversationMeta} data-testid="chat-conversation-time">
        <span>{t('chat.trail.duration', { seconds: conversationSeconds(result.duration_ms) })}</span>
        <InfoTip
          text={t('chat.trail.duration.tip')}
          label={t('common.ui.about', {
            label: t('chat.trail.duration', { seconds: conversationSeconds(result.duration_ms) }),
          })}
          size="xs"
        />
        {conversation ? (
          <Link className={s.trailLink} to={decisionsLink(conversation)} data-testid="chat-view-decisions">
            {t('chat.trail.viewDecisions')}
          </Link>
        ) : null}
      </p>
    </div>
  );
}

/**
 * «Decisões do PDP nesta conversa» (aim-6ph.9): the four states of the search, by `data-state`, and one line per
 * decision, each one a link to the detail of the decision (T17).
 *
 * With the id of the question (aim-6ph.10), the list is the decisions of the id, and the ones of the same person in
 * the window that came without the id go in a separate line, under the approved note (decision 6 of the mrolim). After
 * the 2 minutes of the search, «Atualizar» reads the two lists once more (decision 8).
 */
function PdpDecisions({ conversation, pdp }: { conversation: ConversationWindow; pdp: ConversationDecisions }) {
  const range = conversationRange(conversation);
  const count = pdp.items.length;
  const matchNote = conversation.conversationId
    ? t('chat.trail.pdp.matchNoteById', { sub: conversation.sub })
    : t('chat.trail.pdp.matchNote', {
        sub: conversation.sub,
        from: hourSeconds(range.since) ?? range.since,
        to: hourSeconds(range.until) ?? range.until,
      });
  return (
    <section
      className={s.pdp}
      aria-label={t('chat.trail.pdp.title')}
      data-testid="chat-pdp-decisions"
      data-state={pdp.state}
    >
      <p className={s.pdpTitle}>
        {count > 0 ? t('chat.trail.pdp.titleCount', { n: count }) : t('chat.trail.pdp.title')}
        {count > 0 ? (
          <InfoTip
            text={matchNote}
            label={t('common.ui.about', { label: t('chat.trail.pdp.title') })}
            size="xs"
          />
        ) : null}
      </p>
      {pdp.state === 'searching' ? (
        <p className={s.pdpNote} role="status">
          {t('chat.trail.pdp.waiting')}
        </p>
      ) : null}
      {pdp.state === 'none' ? <p className={s.pdpNote}>{t('chat.trail.pdp.none')}</p> : null}
      {pdp.state === 'error' && pdp.error ? (
        <p className={s.pdpNote}>{t('chat.trail.pdp.error', { reason: errorView(pdp.error).mensagem })}</p>
      ) : null}
      {count > 0 ? (
        <ul className={s.pdpList}>
          {pdp.items.map((decision) => (
            <PdpRow key={decision.request_id} decision={decision} />
          ))}
        </ul>
      ) : null}
      {pdp.withoutId.length > 0 ? (
        <div className={s.pdpWithoutId} data-testid="chat-pdp-without-id">
          <p className={s.pdpNote}>{t('chat.trail.pdp.withoutId')}</p>
          <ul className={s.pdpList} aria-label={t('chat.trail.pdp.withoutId')}>
            {pdp.withoutId.map((decision) => (
              <PdpRow key={decision.request_id} decision={decision} />
            ))}
          </ul>
        </div>
      ) : null}
      {pdp.ended ? (
        <LoadingButton
          className={s.pdpRefresh}
          variant="out"
          size="sm"
          loading={pdp.reading}
          loadingLabel={t('chat.trail.pdp.refreshing')}
          onClick={pdp.refresh}
          data-testid="chat-pdp-refresh"
        >
          {t('chat.trail.pdp.refresh')}
        </LoadingButton>
      ) : null}
    </section>
  );
}

const PDP_STATUS: Record<Decision['verdict'], HopStatus> = {
  permitido: 'ok',
  negado: 'negado',
  indeterminado: null,
};

/**
 * «✓ permitido noc → network-mcp/check_outage · 35 ms · 652b788b ›». A delegation says the method («A2A ·
 * SendMessage»), and a denied decision says the short label of the cause, the same as in Decisions (T16) (decision 3
 * of the mrolim, 11/10/2026). The « · » is punctuation between pieces of data, and it is composed here.
 */
function PdpRow({ decision }: { decision: Decision }) {
  const status = PDP_STATUS[decision.verdict];
  const mark = status ? MARK[status] : EMPTY_MARK;
  const details: string[] = [];
  if (decisionKind(decision) === 'a2a') details.push(`A2A · ${decisionMethod(decision) ?? ''}`.trim());
  if (decision.verdict === 'negado') details.push(causaVocab(causaOf(decision.cause)).rotulo);
  if (decision.duration_ms !== null && decision.duration_ms !== undefined) {
    details.push(`${Math.round(decision.duration_ms)} ms`);
  }
  details.push(decision.request_id.slice(0, 8));
  return (
    <li className={s.pdpRow} data-verdict={decision.verdict} data-request-id={decision.request_id}>
      <Link
        className={s.pdpLink}
        to={`${DECISIONS_PATH}?drawer=${encodeURIComponent(decision.request_id)}`}
      >
        <span className={`${s.mark} ${mark.className}`} aria-hidden="true">
          {mark.symbol}
        </span>
        {/* The spaces stay: the gap of the flex shows a space, and the text that a screen reader or a copy reads does not. */}
        {' '}
        <span className={s.pdpVerdict}>{t(`chat.verdict.${decision.verdict}.label`)}</span>{' '}
        <span>{pdpRowText(decision)}</span>
        <span className={s.pdpDetails}>{` · ${details.join(' · ')}`}</span>
        <span aria-hidden="true">{' ›'}</span>
      </Link>
    </li>
  );
}

const EMPTY_MARK = { symbol: '–', className: s.markEmpty, spokenKey: 'empty' };

function TrailStep({ row }: { row: AskTrailRow }) {
  const mark = row.status ? MARK[row.status] : EMPTY_MARK;
  const tone = row.status ? STEP_TONE[row.status] : s.stepEmpty;
  const title = row.secondary
    ? t('chat.trail.rowTitleWithSecondary', {
        label: row.label,
        text: row.text,
        secondary: row.secondary,
      })
    : t('chat.trail.rowTitle', { label: row.label, text: row.text });
  return (
    <span
      className={`${s.step} ${s[`step_${row.kind}`] ?? ''} ${tone}`}
      title={title}
      data-kind={row.kind}
      data-status={row.status ?? 'indeterminado'}
    >
      <span className={`${s.mark} ${mark.className}`} aria-hidden="true">
        {mark.symbol}
      </span>
      {mark.spokenKey === 'empty' ? null : (
        <span className="sr-only">{t(`chat.trail.mark.${mark.spokenKey}`)}</span>
      )}
      <span className={s.stepLabel}>{row.label}</span>
      <span className={s.stepText}>{row.text}</span>
    </span>
  );
}
